import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import CustomCursor from '../../components/CustomCursor';

const sections = [
  { id: 'overview', label: 'Overview' },
  { id: 'team', label: 'Team' },
  { id: 'sign-up', label: 'Creating an Account' },
  { id: 'login', label: 'Log In' },
  { id: 'auction-page', label: 'Auction Page' },
  { id: 'auction-listings', label: 'Auction Listings' },
  { id: 'user-profile', label: 'User Profile' },
  { id: 'messaging', label: 'Conversations' },
  { id: 'cart', label: 'Your Cart' },
  { id: 'seller-dashboard', label: 'Seller Dashboard' },
  { id: 'home-page', label: 'Main Home Page' },
  { id: 'mobile', label: 'Mobile Optimizations' },
  { id: 'takeaways-results', label: 'Takeaways & Results' },
];

function SideNav() {
  const [activeSection, setActiveSection] = useState('overview');

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY + 120;
      let current = 'overview';
      for (const section of sections) {
        const el = document.getElementById(section.id);
        if (el && el.offsetTop <= scrollY) {
          current = section.id;
        }
      }
      setActiveSection(current);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      window.scrollTo({ top: el.offsetTop - 80, behavior: 'smooth' });
    }
  };

  return (
    <nav className="hidden xl:flex flex-col gap-1 fixed left-8 top-1/2 -translate-y-1/2 z-40 w-[160px]">
      {sections.map((s) => (
        <button
          key={s.id}
          onClick={() => scrollTo(s.id)}
          className={`text-left text-[13px] px-3 py-1.5 rounded-md transition-all cursor-pointer whitespace-nowrap ${
            activeSection === s.id
              ? 'text-notion-accent font-medium bg-notion-accent/10'
              : 'text-notion-secondary hover:text-notion-text hover:bg-notion-hover'
          }`}
        >
          {s.label}
        </button>
      ))}
    </nav>
  );
}

function ComparisonSection({
  title,
  beforeText,
  afterText,
  beforeImage = 'Before Screenshot',
  afterImage = 'After Screenshot',
}: {
  title: string;
  beforeText: string;
  afterText: string;
  beforeImage?: string;
  afterImage?: string;
}) {
  const isBeforeUrl = beforeImage.startsWith('http');
  const isAfterUrl = afterImage.startsWith('http');

  return (
    <div className="mb-20">
      <h2 className="font-serif text-3xl font-medium text-notion-text mb-10">{title}</h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Before */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <span className="w-6 h-6 rounded-full bg-red-500/10 flex items-center justify-center flex-shrink-0">
              <i className="ri-close-line text-red-500 text-sm"></i>
            </span>
            <h3 className="text-[18px] font-medium text-notion-text">Before</h3>
          </div>
          <div className="w-full rounded-lg overflow-hidden border border-notion-border bg-white mb-4">
            {isBeforeUrl ? (
              <img
                src={beforeImage}
                alt="Before screenshot"
                className="w-full h-auto block"
              />
            ) : (
              <div className="w-full h-[400px] flex items-center justify-center">
                <span className="text-notion-secondary text-sm">{beforeImage}</span>
              </div>
            )}
          </div>
          <p className="text-[15px] text-notion-secondary leading-relaxed">{beforeText}</p>
        </div>

        {/* After */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <span className="w-6 h-6 rounded-full bg-green-500/10 flex items-center justify-center flex-shrink-0">
              <i className="ri-check-line text-green-500 text-sm"></i>
            </span>
            <h3 className="text-[18px] font-medium text-notion-text">After</h3>
          </div>
          <div className="w-full rounded-lg overflow-hidden border border-notion-border bg-white mb-4">
            {isAfterUrl ? (
              <img
                src={afterImage}
                alt="After screenshot"
                className="w-full h-auto block"
              />
            ) : (
              <div className="w-full h-[400px] flex items-center justify-center">
                <span className="text-notion-secondary text-sm">{afterImage}</span>
              </div>
            )}
          </div>
          <p className="text-[15px] text-notion-secondary leading-relaxed">{afterText}</p>
        </div>
      </div>
    </div>
  );
}

export default function RaremarqWebPlatformPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBackToPortfolio = () => {
    window.REACT_APP_NAVIGATE('/');
    setTimeout(() => {
      const portfolioSection = document.getElementById('portfolio');
      if (portfolioSection) {
        portfolioSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  return (
    <div className="min-h-screen bg-white font-sans cursor-none">
      <CustomCursor />
      <SideNav />

      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-notion-border">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={handleBackToPortfolio}
              className="flex items-center gap-2 text-[14px] text-notion-secondary hover:text-notion-text transition-colors cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-left-line"></i>
              </span>
              <span>Back to Portfolio</span>
            </button>
            <Link
              to="/"
              className="font-serif text-[18px] font-medium text-notion-text hover:text-notion-accent transition-colors cursor-pointer"
            >
              annie
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-[900px] mx-auto px-6 lg:px-0">
        {/* Hero / Overview */}
        <section id="overview" className="pt-12 lg:pt-16 mb-16">
          <div className="flex items-center gap-2 mb-6">
            <span className="text-[12px] text-notion-secondary bg-notion-tag px-2 py-1 rounded">
              Product Design
            </span>
            <span className="text-[12px] text-notion-secondary bg-notion-tag px-2 py-1 rounded">
              Web App
            </span>
            <span className="text-[12px] text-notion-secondary bg-notion-tag px-2 py-1 rounded">
              2025
            </span>
          </div>
          <h1 className="font-serif text-4xl lg:text-5xl font-medium text-notion-text leading-tight tracking-tight mb-6">
            Raremarq Web Platform
          </h1>
          <p className="text-[17px] text-notion-secondary leading-relaxed mb-4">
            Raremarq is a social marketplace designed to connect collectors, artists, and sellers in a seamless
            buying and selling experience. It offers a curated space for discovering original art, rare collectibles,
            and limited-edition pieces while fostering a community-driven ecosystem.
          </p>
          <p className="text-[17px] text-notion-secondary leading-relaxed mb-4">
            Unlike traditional e-commerce platforms, Raremarq integrates social engagement, auctions, and live
            interactions, making it a unique destination for enthusiasts. We aim to provide one of one experiences.
          </p>
          <p className="text-[17px] text-notion-secondary leading-relaxed mb-10">
            Our goal was to rebrand this community into a space that emphasizes warmth, connection, and a sense of
            premium belonging.
          </p>

          {/* Hero Image Placeholder */}
          <div className="w-full max-w-[800px] rounded-lg overflow-hidden border border-notion-border">
            <img
              src="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/d5015a4c36b3c7ed7f704798b32b25e6.png"
              alt="Raremarq Web Platform Hero"
              className="w-full h-auto object-contain"
            />
          </div>
        </section>

        {/* Project Details */}
        <section className="mb-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-notion-sidebar border border-notion-border rounded-lg p-6">
              <h3 className="text-[12px] font-medium text-notion-secondary mb-2 uppercase tracking-wide">
                My Role
              </h3>
              <p className="text-[15px] text-notion-text font-medium">Founding UX & Ops Designer</p>
            </div>
            <div className="bg-notion-sidebar border border-notion-border rounded-lg p-6">
              <h3 className="text-[12px] font-medium text-notion-secondary mb-2 uppercase tracking-wide">
                Timeline
              </h3>
              <p className="text-[15px] text-notion-text font-medium">Apr 7 – May 9, 2025</p>
            </div>
            <div className="bg-notion-sidebar border border-notion-border rounded-lg p-6">
              <h3 className="text-[12px] font-medium text-notion-secondary mb-2 uppercase tracking-wide">
                Tools
              </h3>
              <p className="text-[15px] text-notion-text font-medium">Figma</p>
            </div>
          </div>
        </section>

        {/* Team */}
        <section id="team" className="mb-16">
          <div className="bg-notion-sidebar border border-notion-border rounded-lg p-8">
            <h3 className="text-[12px] font-medium text-notion-secondary mb-5 uppercase tracking-wide">
              Team
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { initials: 'CT', name: 'Christopher Tung', role: 'Founder' },
                { initials: 'AD', name: 'Annie Ding', role: 'Founding UX & Ops Designer' },
                { initials: 'S', name: 'Scott', role: 'Studio Designer' },
                { initials: 'MB', name: 'Matt Battaglia', role: 'Graphic Designer' },
                { initials: 'JS', name: 'Jacob Singer', role: 'Co-Founder and CTO' },
              ].map((m) => (
                <div key={m.name} className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-notion-accent/15 flex items-center justify-center flex-shrink-0">
                    <span className="text-notion-accent font-medium text-sm">{m.initials}</span>
                  </div>
                  <div>
                    <p className="text-[14px] font-medium text-notion-text">{m.name}</p>
                    <p className="text-[12px] text-notion-secondary">{m.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Old vs New Section Header */}
        <section className="mb-16">
          <h2 className="font-serif text-3xl font-medium text-notion-text mb-4">Old vs New</h2>
          <p className="text-[16px] text-notion-secondary leading-relaxed">
            A comprehensive redesign focused on creating warmth, improving usability, and establishing a cohesive visual
            identity across the entire platform.
          </p>
        </section>

        {/* Creating an Account */}
        <section id="sign-up">
          <ComparisonSection
            title="Creating an Account"
            beforeText="The original sign-up page was functional but lacked emotional connection and social proof. It felt generic and didn't communicate the value of joining the Raremarq community."
            afterText="To build trust and increase conversion, I introduced user testimonials directly on the sign-up page. Since Raremarq centers around community and creative expression, showcasing real voices helps validate the platform's value and reduce hesitation for new users. By placing authentic quotes near the sign-up form, users are reminded of the platform's impact — not from us, but from artists and fans like them. This social proof reinforces credibility and creates an emotional connection at a critical decision point, ultimately driving sign-ups."
            beforeImage="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/55b22ee02db29c035a8d79ccafa608ce.png"
            afterImage="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/705eba701a59cb198cefc496f84312af.png"
          />
        </section>

        {/* Log In */}
        <section id="login">
          <ComparisonSection
            title="Log In"
            beforeText="The original login page felt generic and lacked emotional resonance. It used minimal color, harsh black CTAs, and had no clear connection to the brand's identity or values."
            afterText="I redesigned the page with Raremarq's warm, premium aesthetic — using a soft pink background, branded iconography, and rounded UI elements. The updated form improves readability and user trust while aligning with the full design system. This redesign transformed a sterile entry point into a welcoming, brand-aligned experience — reinforcing user confidence and improving consistency across the product."
            beforeImage="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/28a569a437daad35fa891f86f1655ee6.png"
            afterImage="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/47c00ba3a10f7f5b2df67b72850526b7.png"
          />
        </section>

        {/* Auction Page */}
        <section id="auction-page">
          <ComparisonSection
            title="Auction Page"
            beforeText="No clear discovery mechanism existed. Related items were either missing or visually inconsistent, making it difficult for users to explore beyond a single auction."
            afterText="I introduced a modular, scrollable 'You May Also Like' section that dynamically surfaces related pieces. Users can browse horizontally through recommended items, each clearly labeled with auction status (e.g., 'Live,' 'Featured,' 'Sponsored') and pricing details. This module was designed to: encourage deeper browsing and increase time-on-site, support scalability adapting to different listing types (fixed price, live auction, etc.), and strengthen visual cohesion through consistent card design and color-coded badges. By integrating this scrollable recommendation module, I transformed a static auction page into a more engaging, exploratory experience — helping users discover more and increasing platform stickiness."
            beforeImage="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/7ccc0c9aa53728fcb70dc59f68ca5289.png"
            afterImage="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/672336694204f6134d7262b4c2ff3be5.png"
          />
        </section>

        {/* Auction Listings */}
        <section id="auction-listings">
          <ComparisonSection
            title="Auction Listings"
            beforeText="The original layout was text-heavy and difficult to scan. Listing filters were cluttered and the visual hierarchy made it hard for users to quickly compare auctions or understand urgency."
            afterText="I redesigned the page into a more grid-based, scannable layout that prioritizes discoverability and auction clarity. Listings now feature: visual urgency cues (e.g., countdown timers, 'Featured' and 'Sponsored' badges), consistent card design that scales across devices, and cleaner filter UI for easier narrowing of results. This redesign improves user flow (cards are easier to compare at a glance), engagement (clearer CTAs and urgency increase likelihood of bids), and scalability (badge system supports future auction types). By reorganizing listings into structured, labeled components, I helped users navigate auctions faster while giving sellers more visibility through badge tiers and visual prioritization."
            beforeImage="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/f4b7cc8ae71f5bddab60ad5ebc0d8c00.png"
            afterImage="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/4ab7cd99f9037bbc33b249716cd9acfc.png"
          />
        </section>

        {/* User Profile Page */}
        <section id="user-profile">
          <ComparisonSection
            title="User Profile Page"
            beforeText="The original profile page was cluttered with exposed actions like 'Edit,' 'Delete,' and 'Practice Stream' directly on every content card. This overwhelmed the interface — especially for creators with a large volume of livestreams or artworks. There was no distinction between what creators saw vs. what visitors saw."
            afterText="I redesigned the profile page to feel more curated, structured, and scalable. Key updates include: Introduced a 'Viewing the public version of this page' banner to clearly separate creator and visitor views, improving UX clarity and reducing confusion. Finalized a three-dot overflow menu on each content card to group secondary actions (Edit/Delete), reducing visual noise while preserving full functionality. Streamlined sections like Highlights, All Art, and Hidden Livestreams to improve browsing and modular consistency. The profile redesign focused on decluttering high-density user flows and introducing scalable UI patterns. The public/private view toggle and three-dot overflow were key decisions that balanced control, clarity, and aesthetics."
            beforeImage="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/c3134d072284f0c0331843e32cf17c0e.png"
            afterImage="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/6640ba5ce7d4f0d397821f254861c848.png"
          />
        </section>

        {/* Conversations/Messaging */}
        <section id="messaging">
          <ComparisonSection
            title="Conversations/Messaging"
            beforeText="The original messaging layout was overly basic and difficult to scale. Messages lacked visual hierarchy, the user panel was cramped, and everything shared the same visual weight. DM tips were informative but visually disconnected."
            afterText="I redesigned the conversation system to feel more modern, legible, and scalable. Key improvements include: Structured sidebar with cleaner sender preview and timestamps. Differentiated message styling, including sender-specific bubble colors to improve readability in long threads. Refined layout of DM tips, integrated cleanly with the design system. Improved spacing and padding to enhance readability and focus. This redesign created a clearer, more intuitive messaging experience — one that supports high-volume conversations while feeling secure, clean, and brand-consistent."
            beforeImage="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/388fee9045bb64f536b13758f88994ce.png"
            afterImage="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/e57fec0e712de624f38f11326530c8e2.png"
          />
        </section>

        {/* Your Cart */}
        <section id="cart">
          <ComparisonSection
            title="Your Cart"
            beforeText="The original cart page lacked hierarchy and polish. Key information like pricing, seller details, and payment options were cluttered or visually inconsistent. The payment column lacked emphasis on next steps, and there was no visual reinforcement of trust or urgency."
            afterText="I redesigned the cart to feel more structured, trustworthy, and ready for conversion. Key improvements include: Clear payment section with better grouping and visual spacing. Consistent, modular item cards with thumbnail, description, seller info, and shipping badges. More prominent CTA ('Pay with Credit Card') to guide users forward. Support for multiple cart items from different sellers, displayed cleanly. The redesigned cart improves clarity at checkout, boosts user trust with cleaner seller display, and introduces scalable payment UX for growing order volume."
            beforeImage="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/db988b24efcaa97fb4c890ac5e71aa41.png"
            afterImage="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/d4a8d7742531148d457a4e52afb260e6.png"
          />
        </section>

        {/* Seller Dashboard */}
        <section id="seller-dashboard">
          <ComparisonSection
            title="Seller Dashboard"
            beforeText="The original dashboard was sparse and underpowered — it displayed key metrics (like balance and views) as plain text blocks with minimal hierarchy, no visual indicators, and limited data insights."
            afterText="I redesigned the dashboard to feel data-rich, visually digestible, and scalable for growth-focused creators. Key upgrades include: Data visualization for balance and escrow earnings, with growth indicators and historical trends. Structured tables and cards for performance metrics like popular pieces, total views, and auction history. Improved content density without sacrificing readability, making it easy to scan at a glance. This redesign helps sellers better track earnings, performance, and content traction — making business insights actionable with a clear, visual-first interface."
            beforeImage="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/69498ca95e3292c846521ed2c50e54bb.png"
            afterImage="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/9a2315900aeb3816c2cffd5ec675db61.png"
          />
        </section>

        {/* Main Home Page Redesign */}
        <section id="home-page" className="mb-16">
          <h2 className="font-serif text-3xl font-medium text-notion-text mb-8">Main Home Page Redesign</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Old Home Page - Left */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <span className="w-6 h-6 rounded-full bg-red-500/10 flex items-center justify-center flex-shrink-0">
                  <i className="ri-close-line text-red-500 text-sm"></i>
                </span>
                <h3 className="text-[18px] font-medium text-notion-text">Before</h3>
              </div>
              <div className="w-full rounded-lg overflow-hidden border border-notion-border bg-white">
                <img
                  src="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/7c398bc48311e5188004275c156c001c.png"
                  alt="Old Home Page"
                  className="w-full h-auto block"
                />
              </div>
            </div>

            {/* New Home Page - Right */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <span className="w-6 h-6 rounded-full bg-green-500/10 flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-green-500 text-sm"></i>
                </span>
                <h3 className="text-[18px] font-medium text-notion-text">After</h3>
              </div>
              <div className="w-full rounded-lg overflow-hidden border border-notion-border bg-white">
                <img
                  src="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/ca9f88ba3ab8b9949c536be84863d5a7.png"
                  alt="New Home Page"
                  className="w-full h-auto block"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Mobile Optimizations */}
        <section id="mobile" className="mb-16">
          <h2 className="font-serif text-3xl font-medium text-notion-text mb-6">Mobile Optimizations</h2>
          <p className="text-[16px] text-notion-secondary leading-relaxed mb-8">
            The original platform lacked fully optimized mobile screens — many layouts broke on smaller devices, with misaligned components, poor tap targets, and missing responsive behavior. This made it difficult for users to browse, bid, or manage content on the go.
          </p>
          <p className="text-[16px] text-notion-secondary leading-relaxed mb-8">
            I led the effort to redesign key screens with a mobile-first mindset. Every page was restructured to ensure responsive behavior, clean hierarchy, and seamless interaction across devices. Core actions like bidding, messaging, and managing livestreams are now easily accessible on mobile without compromising brand experience.
          </p>
          <p className="text-[16px] text-notion-secondary leading-relaxed mb-8">
            This redesign significantly improved mobile usability and accessibility — allowing users to interact with the platform confidently from any device.
          </p>
          
          <div className="w-full rounded-lg overflow-hidden border border-notion-border mb-6">
            <img
              src="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/f5e32146e6d1dd4711fc983c2c0641d9.png"
              alt="Mobile optimizations screens 1 through 4"
              className="w-full h-auto block"
            />
          </div>
          <div className="w-full rounded-lg overflow-hidden border border-notion-border mb-6">
            <img
              src="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/c78f41a2a5c55fd0f0d766ab9a5beea5.png"
              alt="Mobile optimizations screens 5 through 8"
              className="w-full h-auto block"
            />
          </div>
        </section>

        {/* Takeaways & Results */}
        <section id="takeaways-results" className="mb-16">
          <h2 className="font-serif text-3xl font-medium text-notion-text mb-6">
            Takeaways &amp; Results
          </h2>
          <p className="text-[16px] text-notion-secondary leading-relaxed mb-8">
            The web platform redesign transformed how users engaged with Raremarq, directly contributing to significant growth in marketplace activity and user trust.
          </p>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            <div className="bg-notion-sidebar border border-notion-border rounded-lg p-6 text-center">
              <div className="text-3xl font-serif font-medium text-notion-accent mb-2">
                3x
              </div>
              <p className="text-[14px] text-notion-secondary">
                Marketplace GMV Tripled YoY in Q4
              </p>
            </div>
            <div className="bg-notion-sidebar border border-notion-border rounded-lg p-6 text-center">
              <div className="text-3xl font-serif font-medium text-notion-accent mb-2">
                $629K
              </div>
              <p className="text-[14px] text-notion-secondary">
                GMV in Q4 2025
              </p>
            </div>
          </div>

          {/* GMV Chart */}
          <div className="w-full max-w-[500px] mx-auto rounded-lg overflow-hidden border border-notion-border bg-white mb-10">
            <img
              src="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/0ae639edbf49f77f5e5446ccd62f0e05.png"
              alt="Raremarq GMV Growth Chart - Q1 2024 to Q4 2025"
              className="w-full h-auto block"
            />
          </div>

          {/* Key Takeaways */}
          <div className="bg-notion-sidebar border border-notion-border rounded-lg p-8">
            <h3 className="text-[14px] font-medium text-notion-secondary uppercase tracking-wide mb-5">
              Key Takeaways
            </h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-notion-accent/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-layout-line text-notion-accent text-sm"></i>
                </div>
                <div>
                  <h4 className="text-[15px] font-medium text-notion-text mb-1">
                    Consistent design systems accelerate trust
                  </h4>
                  <p className="text-[14px] text-notion-secondary leading-relaxed">
                    A unified visual language across all pages helped users feel confident navigating the platform, reducing friction and increasing engagement.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-notion-accent/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-smartphone-line text-notion-accent text-sm"></i>
                </div>
                <div>
                  <h4 className="text-[15px] font-medium text-notion-text mb-1">
                    Mobile optimization unlocks new user segments
                  </h4>
                  <p className="text-[14px] text-notion-secondary leading-relaxed">
                    Responsive redesigns enabled collectors to browse, bid, and manage content on the go — expanding platform accessibility significantly.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-notion-accent/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-user-heart-line text-notion-accent text-sm"></i>
                </div>
                <div>
                  <h4 className="text-[15px] font-medium text-notion-text mb-1">
                    User-centered flows drive conversion
                  </h4>
                  <p className="text-[14px] text-notion-secondary leading-relaxed">
                    Streamlined seller dashboards, clearer auction listings, and improved cart experiences directly contributed to marketplace growth.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-notion-accent/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-bar-chart-grouped-line text-notion-accent text-sm"></i>
                </div>
                <div>
                  <h4 className="text-[15px] font-medium text-notion-text mb-1">
                    Design quality signals platform maturity
                  </h4>
                  <p className="text-[14px] text-notion-secondary leading-relaxed">
                    The polished, premium UI helped position Raremarq as a serious destination for collectors, reinforcing investor and user confidence.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="mb-20">
          <div className="bg-notion-sidebar border border-notion-border rounded-lg p-10">
            <div className="flex flex-col items-center text-center gap-6">
              <div>
                <h3 className="font-serif text-2xl font-medium text-notion-text mb-2">
                  Explore the Brand Identity
                </h3>
                <p className="text-[15px] text-notion-secondary">See how the visual system was created</p>
              </div>
              <div className="flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto">
                <a
                  href="https://raremarq.com/"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 border border-notion-border rounded-lg text-[14px] font-medium text-notion-text hover:bg-notion-hover transition-colors cursor-pointer whitespace-nowrap w-full sm:w-auto"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-external-link-line"></i>
                  </span>
                  Check out Raremarq Live
                </a>
                <Link
                  to="/raremarq-brand-identity"
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-notion-accent text-white rounded-lg text-[14px] font-medium hover:bg-notion-accent/90 transition-colors cursor-pointer whitespace-nowrap w-full sm:w-auto"
                >
                  View Brand Identity
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Footer */}
      <footer className="border-t border-notion-border bg-notion-sidebar">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <Link
                to="/"
                className="font-serif text-[20px] font-medium text-notion-text hover:text-notion-accent transition-colors cursor-pointer"
              >
                annie
              </Link>
              <p className="text-[13px] text-notion-secondary mt-2">
                Product Designer &amp; UX Strategist
              </p>
            </div>
            <div className="flex items-center gap-6">
              <a
                href="mailto:ading@ucdavis.edu"
                className="text-[13px] text-notion-secondary hover:text-notion-accent transition-colors cursor-pointer"
              >
                ading@ucdavis.edu
              </a>
              <a
                href="https://www.linkedin.com/in/annieding04/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[13px] text-notion-secondary hover:text-notion-accent transition-colors cursor-pointer"
              >
                LinkedIn
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
